import { Resource } from "@domain/base/Resource";
import { IBolConnectorRepository } from "@domain/connection/IMarketplaceRepository";
import { Connection } from "@domain/connection/models/Connection";
import { BolConnectorDataSource } from "../datasources/BolConnectorDataSource";
import { ConnectionMapper } from "../mappers/MarketplaceMapper";
import { BolEndpoint } from "@domain/connection/enums/endpoints.enum";
import { InventorySyncEventItem } from '@domain/connection/models/InventorySyncEvent';
import { BolSyncJobStatus } from '@domain/connection/models/BolSyncJob';
import { FullSyncJob } from '@domain/connection/models/ImportJob';



export class BolConnectorRepository implements IBolConnectorRepository {

    constructor(private dataSource: BolConnectorDataSource) { }

    async syncBolMarketplaceConnection(connectionId: string, endpoint: BolEndpoint): Promise<Resource<{ jobId: string }>> {
        try {
            const jobId = await this.dataSource.syncBolConnection(connectionId, endpoint);
            return { status: 'success', data: { jobId } };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to sync marketplace connection'
                }
            };
        }
    }

    async syncSearchTerm(connectionId: string, keyword: string): Promise<Resource<{ jobId: string }>> {
        try {
            const jobId = await this.dataSource.syncSearchTerm(connectionId, keyword);
            return { status: 'success', data: { jobId } };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to sync search term'
                }
            };
        }
    }

    async fetchBolJobStatus(jobId: string): Promise<Resource<BolSyncJobStatus>> {
        try {
            const data = await this.dataSource.fetchBolJobStatus(jobId);
            return { status: 'success', data };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to fetch sync job status'
                }
            };
        }
    }

    async addBolMarketplaceConnection(code: string, redirectUri: string): Promise<Resource<Connection>> {
        try {
            const data = await this.dataSource.addBolMarketplaceConnection(code, redirectUri);
            return {
                status: 'success',
                data: ConnectionMapper.toConnectionModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to add Bol.com connection'
                }
            };
        }
    }

    async fetchInventoryPreview(connectionId: string, source: 'offer' | 'lvb'): Promise<Resource<InventorySyncEventItem[]>> {
        try {
            const data = await this.dataSource.fetchInventoryPreview(connectionId, source);
            return { status: 'success', data: data };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to fetch inventory preview'
                }
            };
        }
    }

    async reconnect(connectionId: string, code: string, redirectUri: string): Promise<Resource<Connection>> {
        try {
            const data = await this.dataSource.reconnect(connectionId, code, redirectUri);
            return { status: 'success', data: ConnectionMapper.toConnectionModel(data) };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to reconnect Bol.com connection'
                }
            };
        }
    }

    async syncFull(connectionId: string): Promise<Resource<FullSyncJob[]>> {
        try {
            const data = await this.dataSource.syncFull(connectionId);
            return { status: 'success', data };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to start full sync'
                }
            };
        }
    }
}